import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, ViewChild, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgChartsModule } from 'ng2-charts';
import { BaseChartDirective } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';
import {
  Chart, CategoryScale, LinearScale, BarElement, BarController,
  Tooltip, Legend, Title,
} from 'chart.js';
import {
  PssAuthService, PssSession, PssPlayerDetails, PssBattle,
  TournamentSummary, TournamentDaySummary, TournamentMemberDay, TournamentMissingAttack,
  TournamentForecast, TournamentForecastBacktest, YaDcUserHistoryEntry,
} from '../../../../services/pss-auth.service';
Chart.register(CategoryScale, LinearScale, BarElement, BarController, Tooltip, Legend, Title);

@Component({
  selector: 'app-tournament-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, NgChartsModule],
  templateUrl: './tournament-panel.component.html',
  styleUrls: ['./tournament-panel.component.css'],
})
export class TournamentPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() session: PssSession | null = null;
  @Input() details: PssPlayerDetails | null = null;
  /** Favorites list owned by the parent, shared across panels. */
  @Input() sharedFavorites: { allianceId: string; allianceName: string }[] = [];
/** The other panel's loaded TournamentSummary, for +/- comparison badges. */
  @Input() peerData: TournamentSummary | null = null;
  /** The other panel's loaded TournamentForecast, for +/- forecast comparison badges. */
  @Input() peerForecastData: TournamentForecast | null = null;
  /** Emits when this panel wants to add/remove a favorite. */
  @Output() favoriteToggled = new EventEmitter<{ allianceId: string; allianceName: string; add: boolean }>();
  /** Emits whenever this panel's tournamentData changes (null = cleared/loading). */
  @Output() tournamentDataChanged = new EventEmitter<TournamentSummary | null>();
  /** Emits whenever this panel's forecastData changes (null = cleared/loading). */
  @Output() forecastDataChanged = new EventEmitter<TournamentForecast | null>();
  /** Emits when the user requests a projection for this month (primary panel only). */
  @Output() projectionRequested = new EventEmitter<TournamentForecastBacktest>();
  /** Whether this panel is the compare panel (suppresses "See Projection" button). */
  @Input() isComparePanel = false;
  /** Backtest data pushed from the parent into the compare panel. */
  @Input() initialBacktest: TournamentForecastBacktest | null = null;

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  private destroy$ = new Subject<void>();

  // ── Controls ──────────────────────────────────────────────────────────────
  selectedAllianceId   = '';
  selectedAllianceName = '';
  selectedMonth = 1;
  selectedYear  = 2026;
  numberOfDays  = 7;

  // ── Fleet search ──────────────────────────────────────────────────────────
  fleetSearchTerm    = '';
  fleetSearchResults: { allianceId: string; allianceName: string; memberCount?: string }[] = [];
  fleetSearchLoading = false;
  fleetSearchError   = '';
  private fleetSearchDebounce: any = null;

  readonly months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' },   { value: 4, label: 'April' },
    { value: 5, label: 'May' },     { value: 6, label: 'June' },
    { value: 7, label: 'July' },    { value: 8, label: 'August' },
    { value: 9, label: 'September' },{ value: 10, label: 'October' },
    { value: 11, label: 'November' },{ value: 12, label: 'December' },
  ];
  readonly yearOptions: number[] = [];

  // ── State ─────────────────────────────────────────────────────────────────
  loading        = false;
  error          = '';
  tournamentData: TournamentSummary | null = null;
  availableMonths: { year: number; month: number }[] = [];

  fleetSectionOpen = true;
  topListType: 'topEarners' | 'attacksMissed' | 'starsPerAttack' = 'topEarners';
  topListMenuOpen  = false;
  selectedDay:    TournamentDaySummary | null = null;
  selectedMember: TournamentMemberDay  | null = null;

  private static readonly TOURNAMENT_CACHE_KEY = 'pss_tournament_summary_cache';

  memberBattles:       PssBattle[] = [];
  memberBattlesLoading = false;
  memberBattlesLoaded  = false;

  // ── Forecast ──────────────────────────────────────────────────────────────
  forecastData:    TournamentForecast | null = null;
  forecastLoading  = false;
  forecastError    = '';
  expandedForecastMemberId: string | null = null;
  forecastMemberHistoriesCache: Record<string, YaDcUserHistoryEntry[]> = {};
  forecastMemberHistoryLoading = false;

  // ── Backtest (projection for past months) ─────────────────────────────────
  backtestData:    TournamentForecastBacktest | null = null;
  backtestLoading  = false;
  backtestError    = '';

  // ── Chart ─────────────────────────────────────────────────────────────────
  chartData: ChartData<'bar'> = { labels: [], datasets: [] };

  chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: ctx => ` ${ctx.parsed.y} stars` } },
    },
    scales: {
      x: { ticks: { color: '#a0b8c8' }, grid: { color: 'rgba(255,255,255,0.05)' } },
      y: { ticks: { color: '#a0b8c8' }, grid: { color: 'rgba(255,255,255,0.08)' }, beginAtZero: true },
    },
  };

  constructor(private auth: PssAuthService) {}

  ngOnInit(): void {
    const now = new Date();
    const lastDayOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate();
    const isTournamentInProgress = now.getUTCDate() >= lastDayOfMonth - 6;

    if (isTournamentInProgress) {
      this.selectedMonth = now.getUTCMonth() + 1;
      this.selectedYear  = now.getUTCFullYear();
    } else {
      const priorMonth   = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
      this.selectedMonth = priorMonth.getUTCMonth() + 1;
      this.selectedYear  = priorMonth.getUTCFullYear();
    }

    for (let y = this.selectedYear; y >= this.selectedYear - 2; y--) {
      (this.yearOptions as number[]).push(y);
    }

    if (this.details?.allianceId) {
      this.selectedAllianceId   = this.details.allianceId;
      this.selectedAllianceName = this.details.fleetName ?? '';
    }

    this.loadTournamentData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialBacktest']) {
      this.applyInitialBacktest();
    }
  }

  private applyInitialBacktest(): void {
    this.backtestData    = this.initialBacktest;
    this.backtestError   = '';
    this.backtestLoading = false;
    this.expandedForecastMemberId     = null;
    this.forecastMemberHistoriesCache = {};
  }

  ngOnDestroy(): void {
    this.tournamentDataChanged.emit(null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Fleet search ──────────────────────────────────────────────────────────

  onFleetSearchInput(): void {
    this.fleetSearchResults = [];
    clearTimeout(this.fleetSearchDebounce);
    const q = this.fleetSearchTerm.trim();
    if (!q || q === this.selectedAllianceName) return;
    this.fleetSearchDebounce = setTimeout(() => this.searchTournamentFleet(), 400);
  }

  searchTournamentFleet(): void {
    clearTimeout(this.fleetSearchDebounce);
    const q = this.fleetSearchTerm.trim();
    if (!q) return;
    this.fleetSearchLoading = true;
    this.fleetSearchError   = '';
    this.fleetSearchResults = [];
    this.auth.searchPssFleets(q)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: results => {
          this.fleetSearchResults = results.filter(r => r.allianceId);
          if (!this.fleetSearchResults.length) this.fleetSearchError = 'No fleets found';
          this.fleetSearchLoading = false;
        },
        error: () => { this.fleetSearchError = 'Search failed'; this.fleetSearchLoading = false; },
      });
  }

  selectTournamentFleet(r: { allianceId: string; allianceName: string }): void {
    this.selectedAllianceId   = r.allianceId;
    this.selectedAllianceName = r.allianceName;
    this.fleetSearchTerm      = '';
    this.fleetSearchResults   = [];
    this.fleetSearchError     = '';
    this.loadTournamentData();
  }

  // ── Favorites (delegated to parent) ───────────────────────────────────────

  isFavorite(allianceId: string): boolean {
    return this.sharedFavorites.some(f => f.allianceId === allianceId);
  }

  fmtDivision(d: string | undefined): string {
    if (!d) return '';
    return d.replace(/^Div([A-D])$/i, (_, l) => 'Div ' + l.toUpperCase());
  }

  toggleFavorite(allianceId: string, allianceName: string): void {
    this.favoriteToggled.emit({ allianceId, allianceName, add: !this.isFavorite(allianceId) });
  }

  selectFavoriteFleet(f: { allianceId: string; allianceName: string }): void {
    this.selectTournamentFleet(f);
  }

  // ── Load ──────────────────────────────────────────────────────────────────

  loadTournamentData(): void {
    const allianceId = this.selectedAllianceId;
    if (!allianceId) { this.error = 'No fleet selected.'; return; }

    this.loading = true;
    this.error   = '';
    this.selectedDay      = null;
    this.selectedMember   = null;
    this.memberBattles    = [];
    this.memberBattlesLoaded  = false;
    this.forecastData     = null;
    this.forecastError    = '';
    this.forecastDataChanged.emit(null);
    this.backtestData     = null;
    this.backtestError    = '';
    this.tournamentData   = null;
    this.tournamentDataChanged.emit(null);

    this.loadAvailableMonths();

    if (this.isUpcomingMonth) { this.loading = false; return; }

    const cached = this.loadCachedTournamentSummary(allianceId, this.selectedMonth, this.selectedYear);
    if (cached) {
      this.tournamentData = cached;
      this.tournamentDataChanged.emit(cached);
      this.rebuildChart(cached);
      this.loading = false;
    }

    this.auth.getTournamentSummary(allianceId, this.selectedMonth, this.selectedYear)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.loading = false;
          if (!data) {
            if (!cached) this.error = 'No tournament data found for this fleet and period.';
            return;
          }
          this.tournamentData = data;
          this.tournamentDataChanged.emit(data);
          this.saveCachedTournamentSummary(allianceId, this.selectedMonth, this.selectedYear, data);
          this.rebuildChart(data);
        },
        error: () => {
          this.loading = false;
          if (!cached) this.error = 'Failed to load tournament data.';
        },
      });
  }

  private loadAvailableMonths(): void {
    const allianceId = this.selectedAllianceId;
    if (!allianceId) return;
    this.auth.getTournamentAvailableMonths(allianceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(months => { this.availableMonths = months; });
  }

  private cacheKey(allianceId: string, month: number, year: number): string {
    return `${allianceId}::${year}-${String(month).padStart(2, '0')}`;
  }

  private loadCachedTournamentSummary(allianceId: string, month: number, year: number): TournamentSummary | null {
    try {
      const raw = localStorage.getItem(TournamentPanelComponent.TOURNAMENT_CACHE_KEY);
      if (!raw) return null;
      const cache = JSON.parse(raw) || {};
      return cache[this.cacheKey(allianceId, month, year)] ?? null;
    } catch { return null; }
  }

  private saveCachedTournamentSummary(allianceId: string, month: number, year: number, summary: TournamentSummary): void {
    try {
      const raw = localStorage.getItem(TournamentPanelComponent.TOURNAMENT_CACHE_KEY);
      const cache = raw ? JSON.parse(raw) : {};
      cache[this.cacheKey(allianceId, month, year)] = summary;
      localStorage.setItem(TournamentPanelComponent.TOURNAMENT_CACHE_KEY, JSON.stringify(cache));
    } catch {}
  }

  selectAvailableMonth(year: number, month: number): void {
    this.selectedYear  = year;
    this.selectedMonth = month;
    this.loadTournamentData();
  }

  availableMonthLabel(year: number, month: number): string {
    return `${this.months[month - 1].label.slice(0, 3)} ${year}`;
  }

  isSelectedMonth(year: number, month: number): boolean {
    return this.selectedYear === year && this.selectedMonth === month;
  }

  // ── Chart ─────────────────────────────────────────────────────────────────

  private rebuildChart(data: TournamentSummary): void {
    const displayDays = data.days.slice(0, this.numberOfDays);
    this.chartData = {
      labels: displayDays.map(d => `Day ${d.dayNumber}`),
      datasets: [{
        label: 'Stars',
        data: displayDays.map(d => d.totalStars),
        backgroundColor: displayDays.map((d, i) => {
          if (d.hasData === false) return 'rgba(120,130,140,0.4)';
          return this.selectedDay?.dayNumber === i + 1 ? 'rgba(0,212,255,0.9)' : 'rgba(0,150,210,0.75)';
        }),
        borderColor: displayDays.map(d =>
          d.hasData === false ? 'rgba(120,130,140,0.5)' : 'rgba(0,212,255,0.6)'
        ),
        borderWidth: 1,
        borderRadius: 4,
      }],
    };
    this.chart?.update();
  }

  onChartWrapperClick(event: MouseEvent): void {
    const ci = (this.chart?.chart) as any;
    if (!ci || !this.tournamentData) return;
    const canvas = (event.currentTarget as HTMLElement).querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const canvasX = event.clientX - rect.left;
    const area = ci.chartArea as { left: number; right: number } | undefined;
    if (!area || canvasX < area.left || canvasX > area.right) return;
    const nBars = this.tournamentData.days.slice(0, this.numberOfDays).length;
    if (nBars === 0) return;
    const idx = Math.min(nBars - 1, Math.max(0, Math.floor((canvasX - area.left) / (area.right - area.left) * nBars)));
    this.onDayBarClick(idx);
  }

  onDayBarClick(idx: number): void {
    if (!this.tournamentData) return;
    const day = this.tournamentData.days[idx];
    if (!day) return;
    if (this.selectedDay?.dayNumber === day.dayNumber) {
      this.selectedDay   = null;
      this.selectedMember = null;
    } else {
      this.selectedDay   = day;
      this.selectedMember = null;
    }
    this.rebuildChart(this.tournamentData);
  }

  // ── Member drill-down ─────────────────────────────────────────────────────

  selectMember(member: TournamentMemberDay): void {
    if (this.selectedMember?.userId === member.userId) {
      this.selectedMember      = null;
      this.memberBattles       = [];
      this.memberBattlesLoaded = false;
      return;
    }
    this.selectedMember      = member;
    this.memberBattles       = [];
    this.memberBattlesLoaded = false;
    if (member.userId === this.session?.userId && this.tournamentData) {
      this.loadMemberBattles(member.userId);
    }
  }

  selectMemberFromTopList(userId: string, userName: string): void {
    this.selectMember({ userId, userName, starsEarned: 0, attacksDone: 0, membershipRank: '', trophies: 0 });
  }

  private loadMemberBattles(userId: string): void {
    if (!this.tournamentData) return;
    this.memberBattlesLoading = true;
    this.auth.getTournamentBattles(userId, this.tournamentData.tournamentStart, this.tournamentData.tournamentEnd)
      .pipe(takeUntil(this.destroy$))
      .subscribe(battles => {
        this.memberBattlesLoading = false;
        this.memberBattlesLoaded  = true;
        this.memberBattles        = battles;
      });
  }

  clearDaySelection(): void {
    this.selectedDay    = null;
    this.selectedMember = null;
    this.memberBattles  = [];
    this.memberBattlesLoaded = false;
    if (this.tournamentData) this.rebuildChart(this.tournamentData);
  }

  clearMemberSelection(): void {
    this.selectedMember = null;
    this.memberBattles  = [];
    this.memberBattlesLoaded = false;
  }

  // ── Display computed values ───────────────────────────────────────────────

  get displayTotalStars(): number {
    if (!this.tournamentData) return 0;
    return this.selectedDay ? this.selectedDay.totalStars : this.tournamentData.totalStars;
  }
  get displayAttacksAvailable(): number {
    if (!this.tournamentData) return 0;
    return this.selectedDay ? (this.selectedDay.members.length || 0) * 6 : this.tournamentData.attacksAvailable;
  }
  get displayAttacksUsed(): number {
    if (!this.tournamentData) return 0;
    return this.selectedDay ? this.selectedDay.totalAttacks : this.tournamentData.attacksUsed;
  }
  get displayPercentageUsed(): number {
    if (!this.tournamentData) return 0;
    if (!this.selectedDay) return this.tournamentData.percentageUsed;
    const avail = this.displayAttacksAvailable;
    return avail > 0 ? Math.round((this.selectedDay.totalAttacks / avail) * 10000) / 100 : 0;
  }
  get displayCard5Value(): number {
    if (!this.tournamentData) return 0;
    return this.selectedDay ? this.selectedDay.members.length : this.tournamentData.trophyMedian;
  }
  get displayActiveCaptains(): number {
    if (!this.tournamentData) return 0;
    if (this.selectedDay) return this.selectedDay.members.filter(m => m.attacksDone > 0).length;
    const active = new Set<string>();
    for (const day of this.tournamentData.days) for (const m of day.members) if (m.attacksDone > 0) active.add(m.userId);
    return active.size;
  }
  get displayAvgStarsPerActiveCaptain(): number {
    if (!this.tournamentData) return 0;
    if (this.selectedDay) {
      const active = this.selectedDay.members.filter(m => m.attacksDone > 0);
      return active.length ? this.selectedDay.totalStars / active.length : 0;
    }
    const totals = new Map<string, { stars: number; attacks: number }>();
    for (const day of this.tournamentData.days)
      for (const m of day.members) {
        const e = totals.get(m.userId) ?? { stars: 0, attacks: 0 };
        e.stars += m.starsEarned; e.attacks += m.attacksDone;
        totals.set(m.userId, e);
      }
    const active = Array.from(totals.values()).filter(v => v.attacks > 0);
    return active.length ? active.reduce((s, v) => s + v.stars, 0) / active.length : 0;
  }
  get displayAvgTrophies(): number {
    if (!this.tournamentData) return 0;
    if (this.selectedDay) {
      const m = this.selectedDay.members;
      return m.length ? m.reduce((s, x) => s + x.trophies, 0) / m.length : 0;
    }
    return this.tournamentData.trophyAvg;
  }
  get displayAvgMaxTrophies(): number {
    if (!this.tournamentData) return 0;
    if (this.selectedDay) {
      const m = this.selectedDay.members;
      return m.length ? Math.max(...m.map(x => x.trophies)) : 0;
    }
    return this.tournamentData.trophyMax;
  }
  get displayAvgStarsPerAttack(): number {
    if (!this.tournamentData) return 0;
    if (this.selectedDay) return this.selectedDay.totalAttacks ? this.selectedDay.totalStars / this.selectedDay.totalAttacks : 0;
    return this.tournamentData.starsPerAttack;
  }

  get forecastPredictedStarsLow(): number {
    if (!this.forecastData || !this.forecastData.members?.length) return 0;
    return this.forecastData.members.reduce((sum, member) => sum + (member.predictedStarsLow || 0), 0);
  }

  get forecastPredictedStarsHigh(): number {
    if (!this.forecastData || !this.forecastData.members?.length) return 0;
    return this.forecastData.members.reduce((sum, member) => sum + (member.predictedStarsHigh || 0), 0);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  get isOwnUser(): boolean { return this.selectedMember?.userId === this.session?.userId; }

  get displayDays(): TournamentDaySummary[] { return this.tournamentData?.days.slice(0, this.numberOfDays) ?? []; }

  get missingSnapshotDays(): number[] { return this.displayDays.filter(d => d.hasData === false).map(d => d.dayNumber); }

  battleOpponentName(battle: PssBattle): string {
    if (!this.selectedMember) return '';
    return battle.attackingUserId === this.selectedMember.userId ? battle.defendingShipName : battle.attackingShipName;
  }

  battleResult(battle: PssBattle): 'win' | 'loss' {
    if (!this.selectedMember) return 'loss';
    const won = (battle.outcomeType === 'Attacker Won' && battle.attackingUserId === this.selectedMember.userId)
             || (battle.outcomeType === 'Defender Won' && battle.defendingUserId === this.selectedMember.userId);
    return won ? 'win' : 'loss';
  }

  memberDaySummary(userId: string): { dayNumber: number; date: string; stars: number; attacks: number }[] {
    if (!this.tournamentData) return [];
    return this.tournamentData.days.map(d => {
      const m = d.members.find(me => me.userId === userId);
      return { dayNumber: d.dayNumber, date: d.date, stars: m?.starsEarned ?? 0, attacks: m?.attacksDone ?? 0 };
    });
  }

  onNumberOfDaysChange(): void { if (this.tournamentData) this.rebuildChart(this.tournamentData); }

  get topListTitle(): string {
    switch (this.topListType) {
      case 'attacksMissed': return 'Attacks Missed';
      case 'starsPerAttack': return 'Stars per Attack';
      default: return 'Top Earners';
    }
  }
  get topListValueHeader(): string {
    switch (this.topListType) {
      case 'attacksMissed': return 'Missed';
      case 'starsPerAttack': return 'Stars/Attack';
      default: return 'Stars';
    }
  }

  get sortedMissedAttackList(): TournamentMissingAttack[] {
    if (!this.tournamentData) return [];
    if (this.selectedDay)
      return [...this.selectedDay.members]
        .map(m => ({ userId: m.userId, userName: m.userName, totalAttacks: m.attacksDone }))
        .sort((a, b) => this.missedAttacksFor(b) - this.missedAttacksFor(a));
    return [...this.tournamentData.missingAttacks].sort((a, b) => this.missedAttacksFor(b) - this.missedAttacksFor(a));
  }

  get topEarnerList(): Array<{ userId: string; userName: string; totalStars: number }> {
    if (!this.tournamentData) return [];
    if (this.selectedDay)
      return [...this.selectedDay.members]
        .map(m => ({ userId: m.userId, userName: m.userName, totalStars: m.starsEarned }))
        .sort((a, b) => b.totalStars - a.totalStars || a.userName.localeCompare(b.userName));
    return [...this.tournamentData.topEarners];
  }

  get starsPerAttackList(): Array<{ userId: string; userName: string; starsPerAttack: number }> {
    if (!this.tournamentData) return [];
    if (this.selectedDay)
      return [...this.selectedDay.members]
        .map(m => ({ userId: m.userId, userName: m.userName, starsPerAttack: m.attacksDone > 0 ? m.starsEarned / m.attacksDone : 0 }))
        .sort((a, b) => b.starsPerAttack - a.starsPerAttack || a.userName.localeCompare(b.userName));
    const totals = new Map<string, { userName: string; stars: number; attacks: number }>();
    for (const day of this.tournamentData.days)
      for (const m of day.members) {
        const e = totals.get(m.userId) ?? { userName: m.userName, stars: 0, attacks: 0 };
        e.stars += m.starsEarned; e.attacks += m.attacksDone;
        totals.set(m.userId, e);
      }
    return Array.from(totals.entries())
      .map(([userId, d]) => ({ userId, userName: d.userName, starsPerAttack: d.attacks > 0 ? d.stars / d.attacks : 0 }))
      .sort((a, b) => b.starsPerAttack - a.starsPerAttack || a.userName.localeCompare(b.userName));
  }

  selectTopListType(type: 'topEarners' | 'attacksMissed' | 'starsPerAttack'): void {
    this.topListType    = type;
    this.topListMenuOpen = false;
  }

  missedAttacksFor(entry: TournamentMissingAttack): number {
    if (!this.tournamentData) return 0;
    const maxAttacks = this.selectedDay ? 6 : this.tournamentData.days.length * 6;
    return Math.max(0, maxAttacks - entry.totalAttacks);
  }

  missedAttacksClass(entry: TournamentMissingAttack): string {
    const missed = this.missedAttacksFor(entry);
    const total  = this.selectedDay ? 6 : (this.tournamentData?.days.length ?? 0) * 6;
    const pct    = total > 0 ? (missed / total) * 100 : 0;
    if (pct === 0) return 'missed-zero';
    if (pct >= 90) return 'missed-high';
    return 'missed-low';
  }

  trackById(_: number, item: { userId: string }): string { return item.userId; }

  formatBattleDate(d: string): string { return d ? d.slice(0, 16).replace('T', ' ') : ''; }

  // ── Forecast ──────────────────────────────────────────────────────────────

  get isUpcomingMonth(): boolean {
    const now      = new Date();
    const nowYear  = now.getUTCFullYear();
    const nowMonth = now.getUTCMonth() + 1;
    if (this.selectedYear  > nowYear)  return true;
    if (this.selectedYear  < nowYear)  return false;
    if (this.selectedMonth > nowMonth) return true;
    if (this.selectedMonth < nowMonth) return false;
    const lastDay = new Date(Date.UTC(nowYear, nowMonth, 0)).getUTCDate();
    return now.getUTCDate() < lastDay - 6;
  }

  loadForecast(): void {
    if (!this.selectedAllianceId) return;
    this.forecastLoading = true;
    this.forecastError   = '';
    this.forecastData    = null;
    this.forecastDataChanged.emit(null);
    this.expandedForecastMemberId = null;
    this.forecastMemberHistoriesCache = {};
    this.forecastMemberHistoryLoading = false;
    this.auth.getTournamentForecast(this.selectedAllianceId, this.selectedMonth, this.selectedYear)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.forecastLoading = false;
          if (!data) { this.forecastError = 'No forecast data available.'; return; }
          this.forecastData = data;
          this.forecastDataChanged.emit(data);
        },
        error: () => { this.forecastLoading = false; this.forecastError = 'Failed to load forecast.'; },
      });
  }

  loadBacktest(): void {
    if (!this.selectedAllianceId) return;
    this.backtestLoading  = true;
    this.backtestError    = '';
    this.expandedForecastMemberId     = null;
    this.forecastMemberHistoriesCache = {};
    this.auth.getTournamentForecastBacktest(this.selectedAllianceId, this.selectedMonth, this.selectedYear)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.backtestLoading = false;
          if (!data) { this.backtestError = 'No projection data available for this month.'; return; }
          this.projectionRequested.emit(data);
        },
        error: () => { this.backtestLoading = false; this.backtestError = 'Failed to load projection.'; },
      });
  }

  get backtestTotalDiff(): number {
    if (!this.backtestData) return 0;
    return this.backtestData.actualTotalStars - this.backtestData.predictedTotalStars;
  }

  forecastConfidenceClass(c: string): string {
    if (c === 'high')   return 'confidence-high';
    if (c === 'medium') return 'confidence-medium';
    return 'confidence-low';
  }

  toggleForecastMember(userId: string): void {
    if (this.expandedForecastMemberId === userId) {
      this.expandedForecastMemberId = null;
      return;
    }
    this.expandedForecastMemberId = userId;
    if (this.forecastMemberHistoriesCache[userId] !== undefined) return;
    this.forecastMemberHistoryLoading = true;
    this.auth.getTournamentMemberHistory(userId, 90)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: history => {
          this.forecastMemberHistoriesCache = { ...this.forecastMemberHistoriesCache, [userId]: history };
          this.forecastMemberHistoryLoading = false;
        },
        error: () => {
          this.forecastMemberHistoriesCache = { ...this.forecastMemberHistoriesCache, [userId]: [] };
          this.forecastMemberHistoryLoading = false;
        },
      });
  }

  forecastMemberTournHistory(userId: string): { month: string; stars: number; attacks: number; fleetName: string; division: string }[] {
    const entries = this.forecastMemberHistoriesCache[userId] ?? [];
    if (!entries.length) return [];

    const entriesByDate = new Map<string, YaDcUserHistoryEntry>();
    for (const entry of entries) {
      const existing = entriesByDate.get(entry.date);
      if (!existing
          || entry.tournamentStars > existing.tournamentStars
          || entry.dailyAttacks > existing.dailyAttacks) {
        entriesByDate.set(entry.date, entry);
      }
    }

    const uniqueEntries = [...entriesByDate.values()];
    const monthSet = new Set<string>(uniqueEntries.map(e => e.date.slice(0, 7)));
    const months = [...monthSet].sort().reverse();
    const result: { month: string; stars: number; attacks: number; fleetName: string; division: string }[] = [];
    const MAX_TOURNAMENT_ATTACKS = 42;

    for (const ym of months) {
      const [yr, mo] = ym.split('-').map(Number);
      const lastDay = new Date(Date.UTC(yr, mo, 0));
      const end = lastDay.toISOString().slice(0, 10);
      const startD = new Date(lastDay);
      startD.setUTCDate(lastDay.getUTCDate() - 6);
      const start = startD.toISOString().slice(0, 10);
      const inWindow = uniqueEntries.filter(e => e.date >= start && e.date <= end);
      if (!inWindow.length) continue;
      const maxStars = Math.max(...inWindow.map(e => e.tournamentStars));
      const totalAttacks = Math.min(MAX_TOURNAMENT_ATTACKS, inWindow.reduce((s, e) => s + e.dailyAttacks, 0));
      const maxEntry = inWindow.reduce((best, e) => e.tournamentStars > best.tournamentStars ? e : best, inWindow[0]);
      const fleetName = maxEntry.allianceName ?? '—';
      const division = maxEntry.division ?? '—';
      if (maxStars > 0) result.push({ month: ym, stars: maxStars, attacks: totalAttacks, fleetName, division });
    }
    return result;
  }

  fmtForecastMonth(ym: string): string {
    const [yr, mo] = ym.split('-').map(Number);
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${names[mo - 1]} ${yr}`;
  }

  // ── Peer comparison helpers ────────────────────────────────────────────────

  /** Format a delta value: "+123", "-45", or "=" when effectively zero. */
  fmtDelta(myVal: number, peerVal: number, decimals = 0): string {
    const d = myVal - peerVal;
    if (parseFloat(d.toFixed(decimals)) === 0) return '=';
    const fmt = d.toFixed(decimals);
    return d > 0 ? '+' + fmt : fmt;
  }

  /** CSS class for a delta: green when this panel is higher, orange when lower. */
  deltaClass(myVal: number, peerVal: number): string {
    const d = parseFloat((myVal - peerVal).toFixed(4));
    if (d === 0) return 'delta-neutral';
    return d > 0 ? 'delta-pos' : 'delta-neg';
  }

  /** Count active captains (attacked at least once) across all days of a summary. */
  peekActiveCaptains(data: TournamentSummary): number {
    const active = new Set<string>();
    for (const day of data.days) for (const m of day.members) if (m.attacksDone > 0) active.add(m.userId);
    return active.size;
  }
}
